import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ScenarioCompareResponse } from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { formatUsd } from '../../lib/format';

const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';
const BASELINE = SX_PALETTE[4]; // amber — "as-is"
const SCENARIO = SX_PALETTE[0]; // brand violet — "what-if"

/** SIL-632 — event-marker label geometry. */
const LABEL_MAX_CHARS = 28;
/** Two labels closer than `horizon / this` months are treated as colliding. */
const MONTHS_PER_CROWDED_LABEL = 9;
const LABEL_FONT_PX = 9.5;
const LABEL_ROW_HEIGHT_PX = 12;
const LABEL_TOP_PX = 4;

/**
 * SIL-632 — a `ReferenceLine` label that truncates instead of overrunning its
 * neighbour, keeping the full text in a native SVG `<title>` (browser-rendered
 * hover, no JS, no state). Recharts injects `viewBox` when it clones this.
 */
function MarkerLabel({ text, row, viewBox }: { text: string; row: number; viewBox?: { x?: number; y?: number } }) {
  const clipped = text.length > LABEL_MAX_CHARS ? `${text.slice(0, LABEL_MAX_CHARS - 1).trimEnd()}…` : text;
  return (
    <text
      x={(viewBox?.x ?? 0) + 4}
      y={(viewBox?.y ?? 0) + LABEL_TOP_PX + row * LABEL_ROW_HEIGHT_PX + LABEL_FONT_PX}
      fontSize={LABEL_FONT_PX}
      fill={MUTED}
    >
      {clipped}
      <title>{text}</title>
    </text>
  );
}

/**
 * Series colors (amber, violet) are a data-encoding scale (DESIGN.md) and
 * fail WCAG AA as *text* on both themes — amber-on-white measures 2.15:1,
 * violet-on-dark-ground 3.08:1. Legend text renders in the standard
 * muted-foreground chart-chrome token instead; a small swatch beside it
 * carries the series-color identity, same split used in FlowSankey.tsx.
 */
function CompareLegend({ payload }: { payload?: Array<{ value?: string; color?: string }> }) {
  if (!payload?.length) return null;
  return (
    <ul className="flex flex-wrap items-center justify-center gap-4 pt-1">
      {payload.map((entry) => (
        <li key={entry.value} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </li>
      ))}
    </ul>
  );
}

/**
 * Baseline-vs-scenario net-position overlay chart (SIL-252 T16, spec §3.5:
 * "built against T10b's locked `series` contract"). Reads `series[].month`,
 * `.baseline.netPositionUsd`, `.scenario.netPositionUsd` directly off the
 * locked `POST /:id/compare` response — no reshaping beyond a flat row per
 * month for recharts.
 */
export function ScenarioCompareChart({
  series,
  height = 260,
  /**
   * SIL-547 (W3) — Compare & decide's additive "chart event markers": one
   * dashed vertical line per distinct cohort action month, so the reader can
   * see WHERE on the curve a sell/refresh/itad actually lands rather than
   * inferring it from the metric table alone. Deliberately keyed by month
   * (not one line per cohort) — two cohorts acting the same month draw one
   * line, not an overlapping pair.
   */
  eventMonths = [],
}: {
  series: ScenarioCompareResponse['series'];
  height?: number;
  eventMonths?: { month: number; label: string }[];
}) {
  const rows = useMemo(
    () =>
      series.map((p) => ({
        month: p.month,
        baselineUsd: p.baseline.netPositionUsd,
        scenarioUsd: p.scenario.netPositionUsd,
      })),
    [series],
  );

  // One line per distinct month — a month with two events (e.g. a sell and
  // a refresh both timed at month 6) renders one line labelled with both,
  // never two overlapping lines.
  //
  // SIL-632 — labels interpolate a user-authored cohort label, so they are
  // arbitrarily long and two markers a couple of months apart used to print
  // straight over each other. Three rules, cheapest first: truncate to
  // `LABEL_MAX_CHARS` (full text stays in the `<title>`), alternate the
  // survivors between two rows, and past two rows drop the label entirely
  // (the dashed line still marks the event, and the month's numbers are in
  // the hover tooltip regardless).
  const markers = useMemo(() => {
    const byMonth = new Map<number, string[]>();
    for (const e of eventMonths) {
      const labels = byMonth.get(e.month) ?? [];
      labels.push(e.label);
      byMonth.set(e.month, labels);
    }
    const horizon = series[series.length - 1]?.month ?? 0;
    // The crowding threshold is ~80px of plot width. The plot is inside a
    // `ResponsiveContainer`, so px aren't known at render — expressed in the
    // one unit that is available, months: ~80px of a ~700px plot spanning
    // `horizon` months. Measuring the container would mean a resize observer
    // for a marker annotation.
    const minGap = Math.max(1, Math.round(horizon / MONTHS_PER_CROWDED_LABEL));
    // Two rows: a label only competes with the previous label on its OWN row,
    // so alternation buys room for a neighbour before anything is dropped.
    const lastMonthInRow = [-Infinity, -Infinity];
    let lastRow = 1;
    return [...byMonth.entries()]
      .map(([month, labels]) => ({ month, label: labels.join(' · ') }))
      .sort((a, b) => a.month - b.month)
      .map((m) => {
        const row = 1 - lastRow;
        if (m.month - (lastMonthInRow[row] ?? -Infinity) < minGap) return { ...m, showLabel: false, row: 0 };
        lastMonthInRow[row] = m.month;
        lastRow = row;
        return { ...m, showLabel: true, row };
      });
  }, [eventMonths, series]);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs" style={{ height, color: MUTED }}>
        No months to project — check the scenario's cohorts.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
        <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          tickFormatter={(m: number) => `M${m}`}
        />
        <YAxis
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={(v: number) => formatUsd(v, { compact: true })}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: BORDER }}
          labelFormatter={(m: number) => `Month ${m}`}
          formatter={(value: number, name: string) => [formatUsd(value), name]}
        />
        <Legend content={<CompareLegend />} />
        {markers.map((m) => (
          <ReferenceLine
            key={m.month}
            x={m.month}
            stroke={MUTED}
            strokeDasharray="2 2"
            label={m.showLabel ? <MarkerLabel text={m.label} row={m.row} /> : undefined}
          />
        ))}
        <Line
          dataKey="baselineUsd"
          name="Baseline (hold)"
          stroke={BASELINE}
          strokeWidth={2}
          strokeDasharray="4 3"
          dot={false}
        />
        <Line dataKey="scenarioUsd" name="Scenario" stroke={SCENARIO} strokeWidth={2.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
